import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatGBP, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass } from "@phosphor-icons/react";

const INVOICE_CLS = {
  draft: "bg-slate-100 text-slate-700", sent: "bg-blue-50 text-blue-800",
  paid: "bg-green-50 text-green-800", written_off: "bg-slate-200 text-slate-500",
};

export default function BillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [matters, setMatters] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    (async () => {
      const [i, m, c] = await Promise.all([api.get("/invoices"), api.get("/matters"), api.get("/clients")]);
      setInvoices(i.data); setMatters(m.data); setClients(c.data);
    })();
  }, []);

  const matterMap = useMemo(() => Object.fromEntries(matters.map((m) => [m.id, m])), [matters]);
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const filtered = invoices.filter((i) => {
    const s = q.toLowerCase();
    const m = matterMap[i.matter_id];
    const client = m ? clientMap[m.client_id] : null;
    const mQ = !s || [i.invoice_number, m?.title, m?.reference, client?.full_name].some((v) => (v || "").toLowerCase().includes(s));
    const mS = status === "all" || i.status === status;
    return mQ && mS;
  });

  const totals = {
    billed: invoices.filter((i) => i.status !== "draft" && i.status !== "written_off").reduce((a, i) => a + i.total, 0),
    paid: invoices.filter((i) => i.status === "paid").reduce((a, i) => a + i.total, 0),
    outstanding: invoices.filter((i) => i.status === "sent").reduce((a, i) => a + i.total, 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Revenue</div>
          <h2 className="font-serif-brand text-3xl">Billing</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="Total Billed" value={formatGBP(totals.billed)} />
        <Kpi label="Paid" value={formatGBP(totals.paid)} />
        <Kpi label="Outstanding" value={formatGBP(totals.outstanding)} accent />
      </div>

      <div className="flex gap-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm h-9 w-64 border-slate-300" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-sm bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="written_off">Written off</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-medium">Invoice #</th>
              <th className="text-left px-6 py-3 font-medium">Matter</th>
              <th className="text-left px-6 py-3 font-medium">Client</th>
              <th className="text-left px-6 py-3 font-medium">Issued</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-right px-6 py-3 font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500">No invoices.</td></tr>}
            {filtered.map((i) => {
              const m = matterMap[i.matter_id];
              const client = m ? clientMap[m.client_id] : null;
              return (
                <tr key={i.id} className="hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono-num text-xs">{i.invoice_number}</td>
                  <td className="px-6 py-3">
                    {m ? (
                      <Link to={`/matters/${m.id}`} className="hover:underline">
                        <div className="font-mono-num text-xs text-slate-500">{m.reference}</div>
                        <div className="text-slate-700">{m.title}</div>
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="px-6 py-3 text-slate-600">{client?.full_name || "—"}</td>
                  <td className="px-6 py-3 text-slate-600">{formatDate(i.issued_at)}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-sm ${INVOICE_CLS[i.status]}`}>{i.status.replace("_", " ")}</span>
                  </td>
                  <td className="px-6 py-3 text-right font-mono-num">{formatGBP(i.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm p-5">
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className={`text-2xl font-medium font-mono-num mt-1 ${accent ? "text-[#9A3412]" : "text-[#0B1528]"}`}>{value}</div>
    </div>
  );
}
